import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import PatientIdBadge from "../components/PatientIdBadge";
import MstMetricsTable from "../components/MstMetricsTable";
import TranscriptionBlock from "../components/TranscriptionBlock";
import { SecureAudio } from "../components/SecureMedia";
import {
  getScanResults,
  reprocessScanAudio,
  ScanResults,
  UnifiedBlockName,
  UnifiedBlockResult,
  updateScanAudioTranscription,
} from "../api/scans";

const BLOCK_TITLES: Record<UnifiedBlockName, string> = {
  animals: "Блок 1 — Животные (вербальная беглость, 60с)",
  park: "Блок 2 — Описание картинки (парк)",
  story: "Блок 3 — Логическая память (история)",
};

const BLOCK_ORDER: UnifiedBlockName[] = ["animals", "park", "story"];

function formatDateTime(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("ru-RU", { timeZone: "Asia/Almaty" });
}

export default function ScanResultsPage() {
  const { scanId } = useParams<{ patientId: string; scanId: string }>();
  const [results, setResults] = useState<ScanResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!scanId) return;
    try {
      const data = await getScanResults(scanId);
      setResults(data);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Не удалось загрузить результаты скана");
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => {
    load();
  }, [load]);

  // Пока какой-то блок обрабатывается — автообновление раз в 5 секунд
  const hasProcessing =
    results != null &&
    Object.values(results.blocks).some((b) => b?.processing_status === "processing");

  useEffect(() => {
    if (!hasProcessing) return;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [hasProcessing, load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !results) {
    return <Alert severity="error">{error || "Результаты не найдены"}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="h5">Результаты единого теста</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={() => { setLoading(true); load(); }}>
          Обновить
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        {results.scan.patient_id && <PatientIdBadge patientId={results.scan.patient_id} />}
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(results.scan.scan_start || results.scan.created_at)}
        </Typography>
        {results.scan.language && (
          <Chip label={`Язык: ${results.scan.language.toUpperCase()}`} size="small" variant="outlined" />
        )}
        {results.scan.operator_email && (
          <Typography variant="body2" color="text.secondary">
            Оператор: {results.scan.operator_email}
          </Typography>
        )}
      </Box>

      {BLOCK_ORDER.map((name) => (
        <BlockSection
          key={name}
          name={name}
          block={results.blocks[name] ?? null}
          onReload={load}
        />
      ))}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Блок 4 — MST (Mnemonic Similarity Task)
        </Typography>
        {results.mst?.metrics ? (
          <MstMetricsTable metrics={results.mst.metrics} nTrials={results.mst.n_trials} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Данные MST ещё не загружены
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

function BlockSection({
  name,
  block,
  onReload,
}: {
  name: UnifiedBlockName;
  block: UnifiedBlockResult | null;
  onReload: () => Promise<void>;
}) {
  const [reprocessing, setReprocessing] = useState(false);

  const handleReprocess = async () => {
    if (!block) return;
    setReprocessing(true);
    try {
      await reprocessScanAudio(block.association_id);
      await onReload();
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {BLOCK_TITLES[name]}
        </Typography>
        {block?.processing_status === "processing" && (
          <Chip icon={<CircularProgress size={12} />} label="Обработка..." size="small" color="info" variant="outlined" />
        )}
        {block?.processing_status === "failed" && (
          <Chip label="Ошибка обработки" size="small" color="error" variant="outlined" />
        )}
        {block?.stt_provider && block.processing_status === "done" && (
          <Chip label={`STT: ${block.stt_provider}`} size="small" variant="outlined" />
        )}
      </Box>

      {!block ? (
        <Typography variant="body2" color="text.secondary">
          Запись этого блока ещё не загружена с iPad
        </Typography>
      ) : (
        <>
          {block.wav_url && <SecureAudio url={block.wav_url} />}

          {block.processing_status === "failed" && (
            <Alert
              severity="warning"
              sx={{ mb: 1 }}
              action={
                <Button
                  size="small"
                  startIcon={reprocessing ? <CircularProgress size={14} /> : <ReplayIcon />}
                  onClick={handleReprocess}
                  disabled={reprocessing}
                >
                  Повторить
                </Button>
              }
            >
              {block.error || "Обработка не удалась"}
            </Alert>
          )}

          {block.processing_status !== "processing" && (
            <TranscriptionBlock
              transcription={block.transcription}
              clusterAnalysis={name === "animals" ? block.cluster_analysis : null}
              onSave={async (text) => {
                await updateScanAudioTranscription(block.association_id, text);
                await onReload();
              }}
            />
          )}

          {block.transcription_edited_by && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Отредактировано: {block.transcription_edited_by}
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}

